import { useState, useRef, useCallback } from 'react';
import { Reply, AtSign } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';
import { FileAttachmentPicker } from './AttachmentWidgets';
import type { TaggableUser } from '../types';
import { useLiveField, LiveDrafts } from '../live/LiveField';

// ── Auto-expanding textarea ──

export function AutoTextarea({ value, onChange, onKeyDown, placeholder, inputRef, autoFocus, liveKey, liveShow = true }: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder: string;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
  autoFocus?: boolean;
  /** Live: the others see what is typed here (live/LiveField.tsx). */
  liveKey?: string | null;
  /** false: only sends what is typed (the thread shows the drafts elsewhere). */
  liveShow?: boolean;
}) {
  const localRef = useRef<HTMLTextAreaElement>(null);
  const ref = inputRef || localRef;
  const live = useLiveField(liveKey ?? null, value);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [ref]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useState(() => { /* sync resize on mount */ });
  // Resize on value change
  if (ref.current) {
    requestAnimationFrame(() => resize());
  }

  return (
    <>
      <textarea
        ref={ref}
        className="form-input--bare"
        value={value}
        onChange={(e) => { onChange(e); requestAnimationFrame(resize); }}
        onKeyDown={onKeyDown}
        onFocus={live.onFocus}
        onBlur={live.onBlur}
        placeholder={placeholder}
        rows={1}
        autoFocus={autoFocus}
      />
      {liveShow && <LiveDrafts drafts={live.drafts} />}
    </>
  );
}

// ── Mention dropdown ──

export function MentionDropdown({ users, selectedIndex, onSelect }: {
  users: TaggableUser[];
  selectedIndex: number;
  onSelect: (user: TaggableUser) => void;
}) {
  const { t } = useT();
  const projectUsers = users.filter(u => u.group === 'project');
  const otherUsers = users.filter(u => u.group !== 'project');

  if (users.length === 0) return null;

  return (
    <div className="mention-dropdown">
      {projectUsers.length > 0 && (
        <>
          <div className="mention-group-label">{t('reply.projectMembers')}</div>
          {projectUsers.map((u, i) => (
            <button
              key={u.id}
              className={`mention-option ${selectedIndex === i ? 'mention-option--selected' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); onSelect(u); }}
            >
              <span className="mention-option-initials">{u.initials}</span>
              <span className="mention-option-name">{u.name}</span>
            </button>
          ))}
        </>
      )}
      {otherUsers.length > 0 && (
        <>
          <div className="mention-group-label">{t('reply.directionAdmin')}</div>
          {otherUsers.map((u, i) => (
            <button
              key={u.id}
              className={`mention-option ${selectedIndex === (projectUsers.length + i) ? 'mention-option--selected' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); onSelect(u); }}
            >
              <span className="mention-option-initials">{u.initials}</span>
              <span className="mention-option-name">{u.name}</span>
              <span className="mention-option-role">{u.group}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}

// ── Mention input hook ──

export function useMentionInput(taggableUsers: TaggableUser[]) {
  const [content, setContent] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState('');
  const [cursorStart, setCursorStart] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [taggedUserIds, setTaggedUserIds] = useState<number[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const filteredUsers = taggableUsers.filter(u =>
    u.name.toLowerCase().includes(filter.toLowerCase())
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setContent(val);
    const cursorPos = e.target.selectionStart || 0;
    const textBeforeCursor = val.slice(0, cursorPos);
    const lastAtIdx = textBeforeCursor.lastIndexOf('@');

    if (lastAtIdx >= 0) {
      const textAfterAt = textBeforeCursor.slice(lastAtIdx + 1);
      const charBeforeAt = lastAtIdx > 0 ? textBeforeCursor[lastAtIdx - 1] : ' ';
      if (/\s/.test(charBeforeAt) || lastAtIdx === 0) {
        setShowDropdown(true);
        setFilter(textAfterAt);
        setCursorStart(lastAtIdx);
        setSelectedIndex(0);
        return;
      }
    }
    setShowDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showDropdown) return;
    if (filteredUsers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filteredUsers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const user = filteredUsers[selectedIndex];
      if (user) insertMention(user);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const insertMention = useCallback((user: TaggableUser) => {
    setContent(prev => {
      const before = prev.slice(0, cursorStart);
      const after = prev.slice(textareaRef.current?.selectionStart || prev.length);
      return `${before}@${user.name} ${after}`;
    });
    setShowDropdown(false);
    if (!taggedUserIds.includes(user.id)) {
      setTaggedUserIds(prev => [...prev, user.id]);
    }
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = cursorStart + user.name.length + 2;
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
        textareaRef.current.focus();
      }
    }, 0);
  }, [cursorStart, taggedUserIds]);

  const reset = () => {
    setContent('');
    setTaggedUserIds([]);
    setShowDropdown(false);
  };

  return {
    content, setContent, showDropdown, filteredUsers, selectedIndex,
    taggedUserIds, setTaggedUserIds, textareaRef,
    handleChange, handleKeyDown, insertMention, reset,
  };
}

// ── Inline reply form ──

interface InlineReplyFormProps {
  replyTo: { id: number; userName: string };
  taggableUsers: TaggableUser[];
  onPost: (content: string, type: 'comment' | 'question' | 'reply', replyToId?: number, taggedUserIds?: number[]) => Promise<number | void>;
  onCancel: () => void;
  projectId?: number;
  onRefresh?: () => void;
  /** false hides the file picker (e.g. the project side panel, where documents are added elsewhere). */
  allowAttachments?: boolean;
  /** Live: the others see the reply as it is typed (liveShow false: shown by the thread instead). */
  liveKey?: string | null;
  liveShow?: boolean;
}

export function InlineReplyForm({ replyTo, taggableUsers, onPost, onCancel, projectId, onRefresh, allowAttachments = true, liveKey, liveShow = true }: InlineReplyFormProps) {
  const { t } = useT();
  const [posting, setPosting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const mention = useMentionInput(taggableUsers);

  const handleSubmit = async () => {
    if (!mention.content.trim() && pendingFiles.length === 0) return;
    setPosting(true);
    try {
      const result = await onPost(mention.content.trim(), 'reply', replyTo.id, mention.taggedUserIds.length > 0 ? mention.taggedUserIds : undefined);
      if (pendingFiles.length > 0 && result && projectId) {
        await api.uploadAttachments(projectId, pendingFiles, { task_update_id: result as number });
      }
      onRefresh?.();
      onCancel();
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="task-update-inline-reply" style={{ position: 'relative' }}>
      <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 'var(--s4)', marginBottom: 'var(--s4)' }}>
        {t('reply.replyingTo', { name: replyTo.userName })}
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 'var(--t-11)' }} onClick={onCancel}>{t('common.cancel')}</button>
      </div>
      <div style={{ position: 'relative' }}>
        <AutoTextarea
          inputRef={mention.textareaRef}
          value={mention.content}
          liveKey={liveKey}
          liveShow={liveShow}
          onChange={mention.handleChange}
          onKeyDown={(e) => {
            mention.handleKeyDown(e);
            // Enter sends (Shift+Enter for a new line), unless it just picked a @-mention.
            if (!e.defaultPrevented && e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          placeholder={t('reply.placeholder')}
          autoFocus
        />
        {mention.showDropdown && <MentionDropdown users={mention.filteredUsers} selectedIndex={mention.selectedIndex} onSelect={mention.insertMention} />}
      </div>
      {mention.taggedUserIds.length > 0 && (
        <div className="tagged-users-bar">
          <AtSign size={11} />
          {taggableUsers.filter(u => mention.taggedUserIds.includes(u.id)).map(u => (
            <span key={u.id} className="tagged-user-chip">
              {u.name}
              <button className="tagged-user-remove" onClick={() => mention.setTaggedUserIds(prev => prev.filter(id => id !== u.id))}>×</button>
            </span>
          ))}
        </div>
      )}
      <div className="task-update-form-actions">
        {allowAttachments && <FileAttachmentPicker files={pendingFiles} onChange={setPendingFiles} />}
        <button className="btn btn-accent btn-sm" disabled={posting || (!mention.content.trim() && pendingFiles.length === 0)} onClick={handleSubmit}>
          <Reply size={14} /> {mention.content.trim() ? t('reply.reply') : t('common.send')}
        </button>
      </div>
    </div>
  );
}
